/**
 * Unbiased randomness for the random-fun tools.
 *
 * Every draw comes from `crypto.getRandomValues`, and every bounded draw uses rejection
 * sampling. `Math.floor(Math.random() * n)` and `value % n` both favour small results
 * whenever `n` does not divide the generator's range; rejection sampling throws away the
 * uneven tail so each of the `n` outcomes is exactly as likely as any other.
 *
 * Never call these during render: the first render is static HTML. Widgets draw inside
 * click handlers.
 */

/** A source of uniformly distributed unsigned 32-bit integers. Injectable for tests. */
export type Uint32Source = () => number;

const TWO_POW_32 = 2 ** 32;
const TWO_POW_53 = 2 ** 53;
/** Dropping the low 11 bits of a 32-bit word leaves the 21 high bits of a 53-bit value. */
const LOW_BITS_DROPPED = 2 ** 11;

export function cryptoUint32(): number {
  const buffer = new Uint32Array(1);
  globalThis.crypto.getRandomValues(buffer);
  return buffer[0] ?? 0;
}

/**
 * A uniform integer in `[0, n)`, for any whole `n` from 1 to 2⁵³.
 *
 * Up to 2³² values it rejects from one 32-bit word; above that it assembles a 53-bit
 * value (21 high bits + 32 low bits), the most a double holds exactly.
 */
export function randomBelow(
  n: number,
  source: Uint32Source = cryptoUint32
): number {
  if (!Number.isInteger(n) || n < 1 || n > TWO_POW_53) {
    throw new RangeError("randomBelow needs a whole number from 1 to 2^53");
  }
  if (n === 1) {
    return 0;
  }
  if (n <= TWO_POW_32) {
    const limit = TWO_POW_32 - (TWO_POW_32 % n);
    for (;;) {
      const value = source();
      if (value < limit) {
        return value % n;
      }
    }
  }
  const limit = TWO_POW_53 - (TWO_POW_53 % n);
  for (;;) {
    const high = Math.floor(source() / LOW_BITS_DROPPED);
    const value = high * TWO_POW_32 + source();
    if (value < limit) {
      return value % n;
    }
  }
}

/** A uniform integer in `[min, max]`, both included. */
export function randomInt(
  min: number,
  max: number,
  source: Uint32Source = cryptoUint32
): number {
  return min + randomBelow(max - min + 1, source);
}

/** A uniformly random element. */
export function pick<T>(
  items: readonly T[],
  source: Uint32Source = cryptoUint32
): T {
  const item = items[randomBelow(items.length, source)];
  if (item === undefined) {
    throw new RangeError("pick needs a non-empty list");
  }
  return item;
}

/** A new array in uniformly random order (Fisher–Yates, from the end). */
export function shuffle<T>(
  items: readonly T[],
  source: Uint32Source = cryptoUint32
): T[] {
  const result = items.slice();
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = randomBelow(i + 1, source);
    const held = result[i] as T;
    result[i] = result[j] as T;
    result[j] = held;
  }
  return result;
}

/** `count` distinct elements in random order — a shuffle that stops early. */
export function sample<T>(
  items: readonly T[],
  count: number,
  source: Uint32Source = cryptoUint32
): T[] {
  const take = Math.min(Math.max(0, Math.trunc(count)), items.length);
  const pool = items.slice();
  for (let i = 0; i < take; i += 1) {
    const j = i + randomBelow(pool.length - i, source);
    const held = pool[i] as T;
    pool[i] = pool[j] as T;
    pool[j] = held;
  }
  return pool.slice(0, take);
}

/**
 * `count` distinct integers from `[0, total)`, uniformly, in random order.
 *
 * Robert Floyd's algorithm: it needs `count` draws and `count` memory however large
 * `total` is, so picking 500 unique minutes out of a million never builds the million.
 */
export function sampleIndices(
  total: number,
  count: number,
  source: Uint32Source = cryptoUint32
): number[] {
  const take = Math.min(Math.max(0, Math.trunc(count)), total);
  const chosen = new Set<number>();
  for (let j = total - take; j < total; j += 1) {
    const r = randomBelow(j + 1, source);
    chosen.add(chosen.has(r) ? j : r);
  }
  return shuffle([...chosen], source);
}
